"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Toast = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_safe_area_context_1 = require("react-native-safe-area-context");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const ThemeContext_1 = require("../ThemeContext");
const Toast = ({ title, message, type = 'success', duration = 3500, onClose, }) => {
    const THEME = (0, ThemeContext_1.useTheme)();
    const { width: windowWidth } = (0, react_native_1.useWindowDimensions)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const insets = (0, react_native_safe_area_context_1.useSafeAreaInsets)();
    const isSmall = size === 'sm';
    const TOAST_WIDTH = 320;
    const animProgress = react_1.default.useRef(new react_native_1.Animated.Value(0)).current;
    const panX = react_1.default.useRef(new react_native_1.Animated.Value(0)).current;
    const handleClose = react_1.default.useCallback(() => {
        react_native_1.Animated.timing(animProgress, {
            toValue: 0,
            duration: 250,
            useNativeDriver: true,
            easing: react_native_1.Easing.in(react_native_1.Easing.ease),
        }).start(() => onClose());
    }, [animProgress, onClose]);
    const panResponder = react_1.default.useRef(react_native_1.PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_, gestureState) => Math.abs(gestureState.dx) > 5,
        onPanResponderMove: (_, gestureState) => {
            if (!isSmall && gestureState.dx < 0) {
                panX.setValue(0);
                return;
            }
            panX.setValue(gestureState.dx);
        },
        onPanResponderRelease: (_, gestureState) => {
            const distance = gestureState.dx;
            if (!isSmall) {
                if (distance > 100) {
                    react_native_1.Animated.timing(panX, {
                        toValue: 400,
                        duration: 150,
                        useNativeDriver: true,
                    }).start(() => onClose());
                }
                else {
                    react_native_1.Animated.spring(panX, {
                        toValue: 0,
                        useNativeDriver: true,
                        bounciness: 8,
                    }).start();
                }
                return;
            }
            if (Math.abs(distance) > 100) {
                const targetX = distance > 0 ? 400 : -400;
                react_native_1.Animated.timing(panX, {
                    toValue: targetX,
                    duration: 150,
                    useNativeDriver: true,
                }).start(() => onClose());
            }
            else {
                react_native_1.Animated.spring(panX, {
                    toValue: 0,
                    useNativeDriver: true,
                    bounciness: 8,
                }).start();
            }
        },
    })).current;
    react_1.default.useEffect(() => {
        react_native_1.Animated.timing(animProgress, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
            easing: react_native_1.Easing.out(react_native_1.Easing.ease),
        }).start();
        const timer = setTimeout(() => {
            handleClose();
        }, duration);
        return () => clearTimeout(timer);
    }, [handleClose, duration]);
    const translateX = animProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [isSmall ? 0 : 350, 0],
    });
    const translateY = animProgress.interpolate({
        inputRange: [0, 1],
        outputRange: [isSmall ? -100 : 0, 0],
    });
    const getBarColor = () => {
        switch (type) {
            case 'error': return THEME.ToastError;
            case 'warning': return THEME.ToastWarning;
            case 'info': return THEME.ToastInfo;
            default: return THEME.Secondary;
        }
    };
    const topStart = size === 'sm' ? 78 : 70;
    const topOffset = insets.top > 0 ? insets.top + topStart : topStart;
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.webRootFix, pointerEvents: "box-none", children: (0, jsx_runtime_1.jsxs)(react_native_1.Animated.View, { ...panResponder.panHandlers, pointerEvents: "auto", accessible: true, accessibilityRole: "alert", style: [
                styles.container,
                {
                    backgroundColor: THEME.OffWhiteGray,
                    top: topOffset,
                    opacity: animProgress,
                    ...(isSmall
                        ? { left: (windowWidth - TOAST_WIDTH) / 2, right: 'auto' }
                        : { right: 20, left: 'auto' }),
                    transform: [
                        { translateX: react_native_1.Animated.add(translateX, panX) },
                        { translateY },
                    ],
                },
            ], children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: [styles.leftBar, { backgroundColor: getBarColor() }] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.content, children: [title ? (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontWeight: 'bold', fontSize: 14, color: THEME.Text }, children: title }) : null, (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { marginTop: 2, fontSize: 12, color: THEME.TextMuted }, children: message })] })] }) }));
};
exports.Toast = Toast;
const styles = react_native_1.StyleSheet.create({
    webRootFix: {
        position: react_native_1.Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'box-none',
    },
    container: {
        position: react_native_1.Platform.OS === 'web' ? 'fixed' : 'absolute',
        width: 320,
        borderRadius: 6,
        flexDirection: 'row',
        overflow: 'hidden',
        elevation: 999999,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
        zIndex: 999999,
    },
    leftBar: { width: 5 },
    content: { padding: 12 },
});
